import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from '@monksflow/monks-ui';
import { useForm } from 'react-hook-form';
import { useRenameProjectFile } from '@/features/manage-project-files/api/useRenameProjectFile.mutation';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import type { RenameFileModalProps } from './RenameFileModal.types';
import { buildRenameFileFormSchema, type RenameFileFormData } from './rename-file.schema';
import { useTranslation } from 'react-i18next';

export function RenameFileModal(props: RenameFileModalProps) {
  const { t } = useTranslation('projects');
  const { t: tValidation } = useTranslation('validation');

  const { open, projectId, file, onClose, onSuccess } = props;

  const defaultValues = useMemo<RenameFileFormData>(
    () => ({ fileName: file?.fileName ?? '' }),
    [file],
  );

  const formSchema = useMemo(() => buildRenameFileFormSchema(tValidation), [tValidation]);

  const form = useForm<RenameFileFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    // Submit-time rather than `onTouched`: the dialog autofocuses this field,
    // so a blur-time check fires the moment a user who emptied it reaches for
    // the close button, and the message it renders grows the centred dialog
    // enough to slide that 16px button out from under the pointer — swallowing
    // the very click that asked to close. The CTA already stays disabled while
    // the name is empty, so the blur check told the user nothing new.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Re-seed the form whenever the modal opens with a different file so each
  // open starts from the freshest server-side name.
  useEffect(() => {
    if (open) form.reset(defaultValues);
  }, [defaultValues, form, open]);

  const renameMutation = useRenameProjectFile({ projectId });
  const isPending = renameMutation.isPending;

  const watchedName = form.watch('fileName');
  const trimmed = watchedName.trim();
  const isUnchanged = !!file && trimmed === file.fileName;
  const canSubmit = !isPending && trimmed.length > 0 && !isUnchanged;

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleClose();
  };

  const onSubmit = form.handleSubmit((formData) => {
    if (!file) return;
    renameMutation.mutate(
      { fileId: file.id, fileName: formData.fileName },
      {
        onSuccess: (updated) => {
          notificationService.showSuccess(t('manageProjectFiles.renameFileModal.fileRenamed'));
          onSuccess?.(updated);
          handleClose();
        },
        onError: (error) => {
          const { status, detail } = parseProblemDetail(
            error,
            t('manageProjectFiles.renameFileModal.failedToRenameFile'),
          );
          if (status === 409) {
            form.setError('fileName', { type: 'server', message: detail });
            return;
          }
          notificationService.showError(detail);
        },
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isPending}
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('manageProjectFiles.renameFileModal.renameFile')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              void onSubmit(event);
            }}
            className="flex flex-col"
          >
            <DialogBody className="flex flex-col gap-4 py-10">
              <FormField
                control={form.control}
                name="fileName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('manageProjectFiles.renameFileModal.fileName')}
                      <span aria-hidden="true" className="text-destructive">
                        *
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('manageProjectFiles.renameFileModal.enterTheFileName')}
                        disabled={isPending}
                        maxLength={255}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                {t('manageProjectFiles.renameFileModal.cancel')}
              </Button>
              <Button type="submit" variant="default" disabled={!canSubmit}>
                {isPending && <Spinner />} {t('manageProjectFiles.renameFileModal.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
